'use no memo';

import type { AssignmentResponseWithDetails } from '@repo/types/assignments';
import type { ColumnDef, ColumnFiltersState, ExpandedState, GroupingState, SortingState, VisibilityState } from '@tanstack/react-table';
import type { ReactNode } from 'react';

import { cn } from '@repo/lib/cn';
import { flexRender, getCoreRowModel, getExpandedRowModel, getFilteredRowModel, getGroupedRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { format } from 'date-fns';
import { ChevronDownIcon, ChevronUpIcon, ColumnsIcon, EyeIcon, PackageIcon, UserIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';

import type { BadgeProps } from '@/components/ui/badge';

import TanstackTableFacetedFilters from '@/components/features/manage/tanstack-table/TanstackTableFacetedFilters';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateDescription, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { SearchInput } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useLocalStorage } from '@/hooks/local-storage';
import { getAvatar } from '@/utils/getAvatar/getAvatar';

type SubmissionStatus = 'unmarked' | 'graded';

const STATUS_BADGE_META: Record<SubmissionStatus, { label: string; variant: BadgeProps['variant'] }> = {
  unmarked: { label: 'Oczekuje', variant: 'warning' },
  graded: { label: 'Oceniono', variant: 'success' },
};

const STATUS_FILTER_OPTIONS = [
  { value: 'unmarked', label: STATUS_BADGE_META.unmarked.label },
  { value: 'graded', label: STATUS_BADGE_META.graded.label },
] satisfies { label: string; value: SubmissionStatus }[];

interface AssignmentSubmissionsTableProps {
  assignmentId: number;
  data: AssignmentResponseWithDetails[];
  groupSelect?: ReactNode;
}

interface AssignmentSubmissionRow extends AssignmentResponseWithDetails {
  userName: string;
  attemptNumber: number;
  status: SubmissionStatus;
  createdAtDate: Date;
}

export default function AssignmentSubmissionsTable(assignmentSubmissionsTableProps: AssignmentSubmissionsTableProps) {
  const { assignmentId, data, groupSelect } = assignmentSubmissionsTableProps;
  const [sorting, setSorting] = useState<SortingState>([]);
  const [grouping, setGrouping] = useState<GroupingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnFilters, setColumnFilters] = useLocalStorage<ColumnFiltersState>(`tanstackTable-assignment-submissions-columnFilters`, []);
  const [columnVisibility, setColumnVisibility] = useLocalStorage<VisibilityState>(`tanstackTable-assignment-submissions-columnVisibility`, {});

  const tableData = useMemo<AssignmentSubmissionRow[]>(() => {
    const byDate = [...data].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    const attemptCounter = new Map<number, number>();
    const attemptNumberById = new Map<number, number>();
    for (const submission of byDate) {
      const count = (attemptCounter.get(submission.userId) ?? 0) + 1;
      attemptCounter.set(submission.userId, count);
      attemptNumberById.set(submission.id, count);
    }

    return data.map((submission) => ({
      ...submission,
      userName: `${submission.user.name} ${submission.user.surname}`.trim(),
      attemptNumber: attemptNumberById.get(submission.id) ?? 1,
      status: submission.gradeId == null ? 'unmarked' : 'graded',
      createdAtDate: new Date(submission.createdAt),
    }));
  }, [data]);

  const columns: ColumnDef<AssignmentSubmissionRow>[] = useMemo(
    () => [
      {
        id: 'userName',
        accessorKey: 'userName',
        meta: 'Uczeń',
        header: 'Uczeń',
        enableHiding: false,
        enableGrouping: true,
        cell: ({ row, getValue }) => {
          const avatarURL = row.original.user.avatarURL;
          if (row.getIsGrouped()) {
            return (
              <div className="flex items-center gap-2 font-medium">
                <Button
                  variant="tertiary"
                  size="sm"
                  aria-expanded={row.getIsExpanded()}
                  data-state={row.getIsExpanded() ? 'open' : 'closed'}
                  onClick={row.getToggleExpandedHandler()}
                  className="h-6 min-h-6 w-6 min-w-6"
                >
                  <ChevronDownIcon className="transition-all group-aria-expanded/button:rotate-180" />
                </Button>

                <Avatar size={'sm'}>
                  <AvatarImage src={getAvatar(avatarURL)} alt={getValue() as string} />
                  <AvatarFallback>
                    <UserIcon />
                  </AvatarFallback>
                </Avatar>
                {getValue() as string}
                <Badge size="sm" variant={'muted'}>
                  {row.subRows.length}
                </Badge>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-2 font-medium">
              <Avatar size={'sm'}>
                <AvatarImage src={getAvatar(avatarURL)} alt={getValue() as string} />
                <AvatarFallback>
                  <UserIcon />
                </AvatarFallback>
              </Avatar>
              <span>{getValue() as string}</span>
            </div>
          );
        },
      },
      {
        id: 'attemptNumber',
        accessorKey: 'attemptNumber',
        meta: 'Próba',
        header: 'Próba',
        cell: ({ row, getValue }) => {
          if (row.getIsGrouped()) return null;
          return getValue();
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        meta: 'Status',
        header: 'Status',
        cell: ({ row, getValue }) => {
          if (row.getIsGrouped()) return null;
          const status = getValue() as SubmissionStatus;
          const meta = STATUS_BADGE_META[status];
          return <Badge variant={meta.variant}>{meta.label}</Badge>;
        },
        filterFn: (row, id, value) => {
          if (!value || value.length === 0) return true;
          return value.includes(row.getValue(id));
        },
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAtDate',
        meta: 'Przesłano',
        header: 'Przesłano',
        cell: ({ row, getValue }) => {
          if (row.getIsGrouped()) return null;
          const value = getValue() as Date;
          return format(value, 'HH:mm dd.MM.yyyy');
        },
      },
      {
        id: 'actions',
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          if (row.getIsGrouped()) return null;
          const submission = row.original;
          return (
            <div className="flex w-full justify-end gap-1">
              <Button variant="outline" size={'sm'} asChild>
                <Link to={`/manage/assignments/${assignmentId}/submission/${submission.id}`}>
                  <EyeIcon />
                  Oceń
                </Link>
              </Button>
            </div>
          );
        },
      },
    ],
    [assignmentId]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onGroupingChange: setGrouping,
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      grouping,
      expanded,
    },
  });

  const groupingLabel = grouping.includes('userName') ? 'Uczeń' : 'Brak';
  const userNameColumn = table.getColumn('userName');
  const statusColumn = table.getColumn('status');

  const handleSearchChange = useCallback(
    (value: string) => {
      userNameColumn?.setFilterValue(value);
    },
    [userNameColumn]
  );

  return (
    <div className={'flex w-full grow flex-col gap-4 overflow-x-hidden'}>
      <div className="bg-background relative h-full w-full space-y-4 overflow-x-auto rounded-2xl border">
        <div className="flex flex-wrap gap-3 px-4 pt-4">
          <TanstackTableFacetedFilters column={statusColumn} title={'Status'} options={STATUS_FILTER_OPTIONS} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                Grupuj
                <Badge size={'sm'} variant={'muted'}>
                  {groupingLabel}
                </Badge>
                <ChevronDownIcon className="transition-all group-aria-expanded/button:rotate-180" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Grupuj według ucznia</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuCheckboxItem
                  checked={grouping.includes('userName')}
                  onCheckedChange={(checked) => {
                    setGrouping(checked ? ['userName'] : []);
                  }}
                >
                  Nazwa ucznia
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={grouping.length === 0} onCheckedChange={() => setGrouping([])}>
                  Brak grupowania
                </DropdownMenuCheckboxItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className={'shrink-0'} size="sm" variant={'outline'}>
                <ColumnsIcon />
                Kolumny
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={'end'}>
              <DropdownMenuLabel>Wybierz kolumny</DropdownMenuLabel>
              <DropdownMenuGroup>
                {table
                  .getAllColumns()
                  .filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanHide())
                  .map((column) => (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) => {
                        column.toggleVisibility(value);
                      }}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {column.columnDef.meta?.toString() ?? ''}
                    </DropdownMenuCheckboxItem>
                  ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          {groupSelect && <div className="w-full sm:ms-auto sm:w-auto">{groupSelect}</div>}
        </div>
        <div className="px-4">
          <SearchInput placeholder={'Szukaj...'} setValue={handleSearchChange} />
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader className={'bg-background/90 sticky top-0 z-10 overflow-hidden border-t backdrop-blur-md'}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className={'hover:bg-surface-primary-hover'}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className="h-full px-3 py-1 whitespace-nowrap"
                      aria-sort={header.column.getIsSorted() === 'asc' ? 'ascending' : header.column.getIsSorted() === 'desc' ? 'descending' : 'none'}
                    >
                      {header.isPlaceholder ? null : header.column.getCanSort() ? (
                        <div
                          className={cn(header.column.getCanSort() && 'flex h-full cursor-pointer items-center justify-between gap-2 select-none')}
                          onClick={header.column.getToggleSortingHandler()}
                          onKeyDown={(e) => {
                            if (header.column.getCanSort() && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              header.column.getToggleSortingHandler()?.(e);
                            }
                          }}
                          tabIndex={header.column.getCanSort() ? 0 : undefined}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: <ChevronUpIcon className="size-4 shrink-0 opacity-60" aria-hidden="true" />,
                            desc: <ChevronDownIcon className="size-4 shrink-0 opacity-60" aria-hidden="true" />,
                          }[header.column.getIsSorted() as string] ?? <span className="size-4" aria-hidden="true" />}
                        </div>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'} className={row.getIsGrouped() ? 'bg-surface-secondary' : ''}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <EmptyState className="pb-11">
                      <EmptyStateCircles className={'z-0'} />
                      <FeaturedIcon size={'lg'} className={'z-10'}>
                        <PackageIcon />
                      </FeaturedIcon>
                      <EmptyStateContent>
                        <EmptyStateTitle>Brak odpowiedzi</EmptyStateTitle>
                        <EmptyStateDescription>Żaden uczeń nie przesłał jeszcze odpowiedzi.</EmptyStateDescription>
                      </EmptyStateContent>
                    </EmptyState>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
